<template>
  <div>
    <v-dialog v-model="showDialog" width="500" persistent>
      <v-carousel
        :cycle="false"
        hide-controls
        v-model="currentPage"
        hide-delimiters
        :continuous="false"
        light
        class="box"
      >
        <v-carousel-item class="box" style="z-index: 5">
          <v-card class="box">
            <v-card-text style="height: 430px">
              <div class="d-flex align-center">
                <h1 class="mb-1">Invite Teammates</h1>
                <v-spacer />
                <v-btn icon variant="plain" @click="close" :disabled="loading"
                  ><v-icon icon="close" /></v-btn
                >
              </div>
              <v-form v-model="valid">
                <v-col style="overflow: auto; height: 350px">
                  <div
                    v-for="(teammate, i) in newTeammates"
                    :key="`teammate_${i}`"
                  >
                    <div class="d-flex">
                      <v-text-field
                        :style="i == 0 ? 'margin-right: 36px' : ''"
                        :label="`Teammate Email ${i + 1}`"
                        v-model="newTeammates[i]"
                        :rules="[validateNotEmpty, inviteEmail]"
                        :data-testid="`invite-email-${i}`"
                        @keyup="changeTeammate(teammate, i)"
                      />
                      <v-btn
                        icon variant="plain"
                        @click="deleteTeammate(teammate, i)"
                        class="delete-teammate"
                        v-show="i != 0"
                        ><v-icon icon="close" /></v-btn
                      >
                    </div>
                    <i
                      v-show="teammateErrors[i]"
                      :key="teammateErrorKey"
                      class="mb-1"
                      style="color: red"
                      >{{ teammateErrors[i] }}</i
                    >
                  </div>
                </v-col>
              </v-form>
            </v-card-text>
            <v-spacer />
            <v-card-actions style="padding: 16px">
              <div class="d-flex align-center">
                <v-btn
                  @click="addTeammate"
                  :disabled="newTeammates.length >= maxTeammates"
                  variant="flat"
                  data-testid="invite-add-another"
                >
                  <v-icon class="mr-1" icon="add" /> Add Another
                </v-btn>
                <v-spacer />
                <v-progress-circular
                  v-show="loading"
                  :indeterminate="true"
                  color="primary"
                  size="32"
                  width="4"
                  class="mr-2"
                />
                <v-btn
                  @click="inviteButtonClicked"
                  data-testid="invite-send"
                  :disabled="!valid || emailError || newTeammates.length == 0"
                  variant="flat"
                  color="primary"
                  class="text-white"
                >
                  Send Invite</v-btn
                >
              </div>
            </v-card-actions>
          </v-card>
        </v-carousel-item>
        <v-carousel-item class="box">
          <v-card class="box" style="height: 88%">
            <v-card-text style="height: 430px" data-testid="invite-sent-panel">
              <h1 class="mb-3">Invitation sent</h1>
              <div class="d-flex justify-center my-6">
                <v-icon
                  icon="check_circle"
                  color="primary"
                  size="96"
                  class="confirmation-check"
                />
              </div>
              <h3 v-if="invitedEmails.length === 1">
                We've emailed an invite to {{ invitedEmails[0] }}. They'll appear
                in your team once they accept.
              </h3>
              <template v-else>
                <h3>
                  We've emailed invites to your teammates. They'll appear in your
                  team once they accept.
                </h3>
                <ul class="mt-3">
                  <li v-for="(email, i) in invitedEmails" :key="`invited_${i}`">
                    {{ email }}
                  </li>
                </ul>
              </template>
            </v-card-text>
            <v-card-actions style="padding: 16px">
              <div class="d-flex align-center">
                <v-spacer />
                <v-btn
                  @click="close"
                  variant="flat"
                  color="primary"
                  class="text-white"
                  data-testid="invite-sent-close"
                  >Close</v-btn
                >
              </div>
            </v-card-actions>
          </v-card>
        </v-carousel-item>
      </v-carousel>
    </v-dialog>
  </div>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import _ from "lodash";
import { mapGetters } from "vuex";
import rules from "@/mixins/rules";
import helpers from "@/mixins/helpers";
import { notifyTeamChanged } from "@/stores/sidebarRefresh";
import { inviteEmail } from "@/utils/validation";

// The backend accepts at most this many addresses per invite request
// (MAX_INVITES_PER_REQUEST in api/src/endpoints/auth.py).
const MAX_TEAMMATES = 25;

export default {
  name: "InviteTeammateDialog",

  props: {
    teammates: {
      type: Array,
      required: false,
    },

    currentSubscriptionCost: {
      required: true,
    },
  },

  mixins: [helpers, rules],

  watch: {},

  data() {
    return {
      newTeammates: [""],
      maxTeammates: MAX_TEAMMATES,
      // Snapshot of the emails we actually sent invites to, captured at send
      // time so the confirmation panel can name them even after close() resets
      // newTeammates back to [""].
      invitedEmails: [],
      teammateErrors: [],
      valid: null,
      showDialog: false,
      emailError: false,
      emailErrorText: "",
      loading: false,
      teammateErrorKey: 0,
      currentPage: 0,
      inviteSent: false,
    };
  },

  mounted() {},

  methods: {
    changeTeammate(email, index) {
      let isDuplicate = false;
      // remove potential duplicate teammate error
      if (this.teammateErrors[index] == "Email listed more than once") {
        isDuplicate = true;
      }

      this.teammateErrors[index] = null;
      if (isDuplicate) {
        this.teammateErrors[this.newTeammates.indexOf(email)] = null;
      }

      this.teammateErrorKey++;
    },

    addTeammate() {
      if (this.newTeammates.length >= MAX_TEAMMATES) {
        return;
      }
      this.newTeammates.push("");
    },

    inviteEmail,

    deleteTeammate(email, index) {
      let isDuplicate = false;
      // remove potential duplicate teammate error
      if (this.teammateErrors[index] == "Email listed more than once") {
        isDuplicate = true;
      }

      this.teammateErrors.splice(index, 1);
      this.newTeammates.splice(index, 1);

      if (isDuplicate) {
        this.teammateErrors[this.newTeammates.indexOf(email)] = null;
      }

      this.teammateErrorKey++;
    },

    show() {
      this.showDialog = true;
    },

    close() {
      if (this.inviteSent) {
        this.$emit("invite-sent", this.newTeammates);
      }
      this.newTeammates = [""];
      this.invitedEmails = [];
      this.teammateErrors = [];
      this.teammateErrorKey++;
      this.showDialog = false;
      this.currentPage = 0;
      this.inviteSent = false;
    },

    toggle() {
      this.showDialog = !this.showDialog;
    },

    // returns a list of booleans
    // * each boolean and it's index represents whether the teammate email
    // * * at that index is not currently assigned to a user in the db
    async validateTeammates() {
      let valid = true;
      let response;
      this.teammateErrors = [];

      // Refuse addresses the backend would reject before asking it anything.
      this.newTeammates.forEach((value, index) => {
        const verdict = inviteEmail(value);
        if (verdict !== true) {
          this.teammateErrors[index] = verdict;
          valid = false;
        }
      });
      if (!valid) {
        this.teammateErrorKey++;
        return false;
      }

      try {
        response = await this.$axios.post("/auth/getTeammatesEmailsUnique", {
          teammates: this.newTeammates,
        });
      } catch (err) {
        console.log(describeError(err));
        this.$store.dispatch("raiseError", err);
      }

      this.newTeammates.forEach((value_x, index) => {
        let count = 0;
        this.newTeammates.forEach((value_y) => {
          if (value_x == value_y) {
            count++;
          }
          if (count > 1) {
            this.teammateErrors[index] = "Email listed more than once";
            valid = false;
          }
        });
        if (this.teammates) {
          this.teammates.forEach((account) => {
            if (account["email"] == value_x) {
              this.teammateErrors[index] = "Email already on team";
              valid = false;
            }
          });
        }
      });

      // A refused check (the error is already raised above) cannot confirm
      // the addresses are free, so do not let the invite go out.
      if (!response || !Array.isArray(response.data)) {
        this.teammateErrorKey++;
        return false;
      }

      let uniqueList = response.data;
      uniqueList.forEach((value, index) => {
        if (!value) {
          this.teammateErrors[index] = "Email already in use";
          valid = false;
        }
      });

      this.teammateErrorKey++;

      return valid;
    },

    // Teammates are free -- there is no order/cost/checkout step. The email
    // panel's action button both validates AND sends the invite, advancing to
    // the confirmation panel only on a successful send (inviteSent guard).
    async inviteButtonClicked() {
      let valid = await this.validateTeammates();
      if (!valid) {
        return;
      }

      // Snapshot the emails before the send/close cycle can mutate the list.
      const emails = [...this.newTeammates];
      const sent = await this.sendInviteTeammatesEvent();
      if (sent) {
        this.invitedEmails = emails;
        this.currentPage++;
      }
    },

    // Resolve the acting account id for the subscription event POSTs.
    //
    // Audit finding F4 (docs/planning/2026-06-24-impersonation-correctness-audit.md):
    // guarded so a null `state.account` returns null instead of throwing.
    // Under SA impersonation this is the SA's OWN id (state.account hydrates
    // from the caller's own row since #2997), which is not the tenant scope.
    // The backend maps it -- and null -- to the impersonated tenant's Owner
    // (_resolve_scope_user_id in api/src/endpoints/subscription.py). For a
    // non-impersonating caller their own id flows through unchanged.
    actingUserId() {
      const account = this.$store.state.account;
      return account && account._jv ? account._jv.id : null;
    },

    async sendInviteTeammatesEvent() {
      this.loading = true;
      try {
        await this.$axios.post(`/subscription/sendInviteTeammateEvent/`, {
          user_id: this.actingUserId(),
          teammates: this.newTeammates,
          source: "distributor",
        });
        this.inviteSent = true;
        // Inviting a teammate adds a member; refresh the TEAM sidebar count.
        // (An invited-but-not-yet-accepted member may not carry a role_id yet,
        // so the by-role tally may not move until they accept -- but a redundant
        // refetch is harmless, and it refreshes once the next mutation fires.)
        // Interim seam pending store-ification (https://trello.com/c/g9K52FsA).
        notifyTeamChanged();
        return true;
      } catch (err) {
        console.log(describeError(err));
        this.$store.dispatch("raiseError", err);
        return false;
      } finally {
        this.loading = false;
      }
    },

    async inviteTeammates() {
      this.loading = true;
      try {
        await this.$axios.post(`/subscription/inviteCreateTeammates/`, {
          user_id: this.actingUserId(),
          teammates: this.newTeammates,
          source: "distributor",
        });
        this.inviteSent = true;
        return true;
      } catch (err) {
        console.log(describeError(err));
        this.$store.dispatch("raiseError", err);
        return false;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.delete-teammate {
  margin: 0px;
  margin-top: 13px;
}

.box {
  height: 500px !important;
  width: 500px !important;
}
</style>
